import type { PatchOrderRequestBody } from '@paypal/paypal-js';
import { ENDPOINT } from '../constants';
import type {
    ApiRequestParams,
    ApiResponse,
    GetBraintreeResponse,
    GetShippingMethodsQueryParams,
    OrderReference,
    RequestParams,
    UpdateShippingMethodBody,
    User,
    ExperimentsResponse
} from '../typings/api';
import { METHOD, OrderType, ReferenceType, ReferenceValueType } from '../typings/api';
import type { ClientSettings, WidgetSettings } from '../typings/configuration.types';
import type {
    ActiveBasketSummaryData,
    BasketCompleteRequest,
    BasketPaymentMethod,
    BraintreeTokenData,
    CompleteOrderData,
    DeliveryAddressInfo,
    ShippingMethod,
    ValidateForterRequest,
    ValidationEvent,
} from '../typings/basket.types';
import { Logger } from '../utils';
import getCookieValue from '../utils/getCookieValue';
import type { PAYMENT_TYPES } from '../typings/payment.types';
import { AuthorizationError, ErrorCodeResponseError } from '../errors';

interface ErrorCodePayload {
    errorCode?: number;
}

const hasErrorCode = (data: unknown): data is ErrorCodePayload =>
    typeof data === 'object' && data !== null && 'errorCode' in data;

export class Api {
    private configuration: WidgetSettings;

    constructor(configuration: WidgetSettings) {
        this.configuration = configuration;
    }

    async get<T>(request: RequestParams): Promise<T> {
        return this.processRequest<T>(request, METHOD.GET);
    }

    async post<T>(request: RequestParams): Promise<T> {
        return this.processRequest<T>(request, METHOD.POST);
    }

    async patch<T>(request: RequestParams): Promise<T> {
        return this.processRequest<T>(request, METHOD.PATCH);
    }

    async put<T>(request: RequestParams): Promise<T> {
        return this.processRequest<T>(request, METHOD.PUT);
    }

    async processRequest<T>(request: RequestParams, method: METHOD) {
        const { success, data } = await this.fetch<T>({
            method,
            urlPart: request.urlPart,
            initialHeaders: request.initialHeaders,
            body: request.body,
        });

        if (!success) {
            const printBody = method !== METHOD.GET;
            const message = `Can't ${method} ${request.urlPart}${
                printBody ? ` with ${JSON.stringify(request.body)}` : ''
            }; response data: ${JSON.stringify(data)}}`;

            if (data instanceof Error) {
                throw data;
            }

            if (hasErrorCode(data) && data.errorCode && Number.isInteger(data.errorCode)) {
                throw new ErrorCodeResponseError(data.errorCode, message);
            }

            throw new Error(message);
        } else {
            return data;
        }
    }

    async getClientSettings() {
        return this.get<ClientSettings>({
            urlPart: ENDPOINT.clientSettings(),
        });
    }

    async getExperimentsTree() {
        return this.get<ExperimentsResponse>({
            urlPart: ENDPOINT.experimentsTree(),
            initialHeaders: {
                visitor: getCookieValue('Visitor'),
            },
        });
    }

    async getBraintreeToken(basketId: number) {
        return this.get<GetBraintreeResponse>({
            urlPart: ENDPOINT.braintreeToken(),
            initialHeaders: {
                'x-basket-id': basketId,
            },
        });
    }

    async getActiveBasketSummary() {
        return this.get<ActiveBasketSummaryData>({
            urlPart: ENDPOINT.activeBasketSummary(),
        });
    }

    async updatePaypalOrder(payPalOrderId: string, payload: PatchOrderRequestBody) {
        return this.patch<null>({
            urlPart: ENDPOINT.updateOrder(payPalOrderId),
            body: payload,
        });
    }

    async updateDeliveryDetails(deliveryDetails: DeliveryAddressInfo, basketId: number) {
        const reference: OrderReference = {
            referenceType: ReferenceType.Order,
            orderType: OrderType.Buying,
            valueType: ReferenceValueType.Id,
            value: basketId.toString(),
        };
        return this.put<null>({
            urlPart: ENDPOINT.deliveryDetails(),
            body: {
                reference,
                payload: deliveryDetails,
            },
        });
    }

    async updateCustomerEmail(email: string, orderNumber: string, basketId: number) {
        return this.patch<null>({
            urlPart: ENDPOINT.customerEmail(orderNumber),
            body: { email },
            initialHeaders: {
                'x-basket-id': basketId,
            },
        });
    }

    async updateShippingMethod(body: UpdateShippingMethodBody, basketId: number) {
        return this.put<ActiveBasketSummaryData>({
            urlPart: ENDPOINT.shippingMethod(),
            body,
            initialHeaders: {
                'x-basket-id': basketId,
            },
        });
    }

    async getShippingMethods(basketId: number, query?: GetShippingMethodsQueryParams) {
        let searchParams: URLSearchParams | undefined;

        if (query) {
            searchParams = new URLSearchParams();
            for (const key of Object.keys(query) as (keyof GetShippingMethodsQueryParams)[]) {
                const value = query[key];
                if (value !== undefined) {
                    searchParams.append(key, value);
                }
            }
        }

        return this.get<ShippingMethod[]>({
            urlPart: ENDPOINT.shippingMethods(basketId, searchParams),
        });
    }

    async getActiveBasketPaymentMethods() {
        return this.get<BasketPaymentMethod[]>({
            urlPart: ENDPOINT.activeBasketPaymentMethods(),
        });
    }

    async getBraintreeTokenForGuestBasket() {
        return this.get<BraintreeTokenData>({
            urlPart: ENDPOINT.paymentToken(),
        });
    }

    async completeBasket(orderData: BasketCompleteRequest) {
        return this.post<CompleteOrderData>({
            urlPart: ENDPOINT.completeBasket(),
            body: orderData,
            initialHeaders: {
                'x-basket-id': orderData.basketId,
            },
        });
    }

    async allocate(basketId: number, paymentType: PAYMENT_TYPES) {
        return this.post<Date | undefined>({
            urlPart: ENDPOINT.allocate(),
            initialHeaders: {
                'x-basket-id': basketId,
                'x-caller': 'express',
                'x-payment-method-type': paymentType,
            },
        });
    }

    async forterCancel(basketId: number) {
        return this.post<boolean>({
            urlPart: ENDPOINT.forterCancel(),
            body: {
                basketId,
            },
            initialHeaders: {
                'x-basket-id': basketId,
            },
        });
    }

    async forterValidate(validateForterRequest: ValidateForterRequest) {
        return this.post<ValidationEvent>({
            urlPart: ENDPOINT.forterValidate(),
            body: validateForterRequest,
            initialHeaders: {
                'x-basket-id': validateForterRequest.basketId,
            },
        });
    }

    async getUser() {
        return this.get<User>({
            urlPart: ENDPOINT.getUser(),
        });
    }

    private async fetch<T>(request: ApiRequestParams): Promise<ApiResponse<T>> {
        const url = `${this.configuration.apiBasePath}/${request.urlPart}`;
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            basketToken: getCookieValue('guest_token'),
            ...Object.fromEntries(
                Object.entries(request.initialHeaders ?? {}).map(([key, value]) => [key, String(value ?? '')]),
            ),
        };

        if (this.configuration.authToken) {
            headers.Authorization = `Bearer ${this.configuration.authToken}`;
        }

        const options: RequestInit = {
            headers,
            method: request.method,
            mode: 'cors',
            credentials: 'include',
        };

        if (request.body) {
            options.body = JSON.stringify(request.body);
        }

        try {
            const response = await fetch(url, options);

            if (!response.ok) {
                if (response.status === 400) {
                    Logger.showLog('error', 'Bad request. Please, check your request.');
                }

                if (response.status === 401) {
                    Logger.showLog('error', 'Unauthorized. Please, check your request.');

                    throw new AuthorizationError('Unable to process request.');
                }

                if (response.status >= 500) {
                    Logger.showLog('error', 'Server errors.');
                }

                return { success: false, data: (await response.json()) as T };
            }

            if (response.status === 204) {
                return { success: true, data: {} as T };
            }

            const data = (await response.json()) as T;

            return { success: true, data };
        } catch (error: unknown) {
            return { success: false, data: error as T };
        }
    }
}
